import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api, getApiErrorCode, getApiErrorMessage, isEmailVerificationRequiredError } from '@/services/api';
import { featureFlags } from '@/config/featureFlags';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Upload,
  ArrowLeft,
  Loader2,
  Sparkles,
  Copy,
  RefreshCw,
  Info,
  FileText,
  Crown,
  Download,
  ChevronDown
} from 'lucide-react';
import MainMenu from '@/components/MainMenu';
import SavedResumeSelector from '@/components/SavedResumeSelector';
import {
  getUnsupportedUploadFormatMessage,
  isSupportedUploadFile,
  LEGACY_DOC_GUIDANCE,
  SUPPORTED_UPLOAD_FORMAT_LABEL,
  UPLOAD_ACCEPT,
} from '@/lib/uploadFormats';

function AIDisclosure() {
  return (
    <div className="flex items-start gap-2 p-3 bg-accent/10 border border-accent/20 rounded-lg text-sm">
      <Sparkles className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
      <p className="text-muted-foreground">
        <span className="font-medium text-foreground">AI-generated content.</span>{' '}
        Review and personalize the final letter before sending it.
      </p>
    </div>
  );
}

export default function CoverLetterPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const savedResumesEnabled = featureFlags.savedResumes;

  const [resumeText, setResumeText] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [generatedCoverLetter, setGeneratedCoverLetter] = useState('');
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [savedResumesAvailable, setSavedResumesAvailable] = useState(savedResumesEnabled);
  const [savedResumes, setSavedResumes] = useState([]);
  const [savedResumesLoading, setSavedResumesLoading] = useState(false);
  const [loadingSavedResume, setLoadingSavedResume] = useState(false);
  const [selectedSavedResumeId, setSelectedSavedResumeId] = useState('');
  const [replacementNotice, setReplacementNotice] = useState('');
  const [pendingUploadedResumeSave, setPendingUploadedResumeSave] = useState(null);
  const effectivePlan = user?.effective_plan || user?.plan || 'free';

  const hasPremiumAccess = user?.is_admin || effectivePlan === 'premium';
  const savedResumeUiEnabled = savedResumesEnabled && savedResumesAvailable;

  const buildSavedResumeNameFromFilename = (filename) => {
    const cleanedFilename = (filename || '').trim();
    if (!cleanedFilename) {
      return undefined;
    }

    const filenameWithoutExtension = cleanedFilename.replace(/\.[^.]+$/, '').trim();
    return filenameWithoutExtension || cleanedFilename;
  };

  const hideSavedResumeUi = useCallback(() => {
    setSavedResumesAvailable(false);
    setSavedResumes([]);
    setSelectedSavedResumeId('');
    setPendingUploadedResumeSave(null);
  }, []);

  const refreshSavedResumes = useCallback(async ({ showErrorToast = false } = {}) => {
    if (!savedResumesEnabled || !savedResumesAvailable || !hasPremiumAccess) {
      return;
    }

    setSavedResumesLoading(true);
    try {
      const data = await api.listSavedResumes();
      setSavedResumes(Array.isArray(data?.saved_resumes) ? data.saved_resumes : []);
    } catch (error) {
      if (getApiErrorCode(error) === 'saved_resumes_disabled') {
        hideSavedResumeUi();
        return;
      }

      if (showErrorToast) {
        toast.error(getApiErrorMessage(error, 'Failed to load saved resumes'));
      }
    } finally {
      setSavedResumesLoading(false);
    }
  }, [hasPremiumAccess, hideSavedResumeUi, savedResumesAvailable, savedResumesEnabled]);

  useEffect(() => {
    if (!savedResumesEnabled || !hasPremiumAccess) {
      return;
    }

    refreshSavedResumes({ showErrorToast: true });
  }, [hasPremiumAccess, refreshSavedResumes, savedResumesEnabled]);

  const attemptSavePendingUploadedResume = async (currentResumeText) => {
    if (!savedResumeUiEnabled || !pendingUploadedResumeSave) {
      return currentResumeText;
    }

    const pendingResumeText = (pendingUploadedResumeSave.resumeText || '').trim();
    const currentTrimmedResumeText = (currentResumeText || '').trim();
    setPendingUploadedResumeSave(null);

    if (!pendingResumeText || pendingResumeText !== currentTrimmedResumeText) {
      return currentResumeText;
    }

    const payload = {
      resume_text: pendingResumeText,
      source: 'uploaded',
    };
    const savedResumeName = buildSavedResumeNameFromFilename(pendingUploadedResumeSave.filename);
    if (savedResumeName) {
      payload.name = savedResumeName;
    }

    try {
      const savedResume = await api.createSavedResume(payload);

      if (savedResume?.duplicate) {
        const existingResumeId = savedResume?.existing_resume?.id;
        if (existingResumeId) {
          try {
            const existingResume = await api.getSavedResume(existingResumeId);
            if (existingResume?.resume_text) {
              setResumeText(existingResume.resume_text);
              setSelectedSavedResumeId(existingResumeId);
              toast.info('This resume is already saved. We loaded the existing saved version instead.');
              await refreshSavedResumes();
              return existingResume.resume_text;
            }
          } catch (error) {
            if (getApiErrorCode(error) === 'saved_resumes_disabled') {
              hideSavedResumeUi();
              return currentResumeText;
            }

            toast.error(getApiErrorMessage(error, 'This resume is already saved, but the saved version could not be loaded right now.'));
            await refreshSavedResumes();
            return currentResumeText;
          }
        }

        toast.info('This resume is already saved. We loaded the existing saved version instead.');
        await refreshSavedResumes();
        return currentResumeText;
      }

      if (savedResume?.id) {
        setSelectedSavedResumeId(savedResume.id);
      }
      await refreshSavedResumes();
      return currentResumeText;
    } catch (error) {
      const errorCode = getApiErrorCode(error);

      if (errorCode === 'saved_resumes_disabled') {
        hideSavedResumeUi();
        return currentResumeText;
      }

      if (errorCode === 'saved_resume_limit_reached') {
        toast.info('Saved resume limit reached. You can keep using this uploaded resume, but it was not saved. Delete an old saved resume to save a new one.');
        return currentResumeText;
      }

      toast.error(getApiErrorMessage(error, 'Uploaded resume could not be saved for later.'));
      return currentResumeText;
    }
  };

  const handleSavedResumeSelect = async (savedResumeId) => {
    if (!savedResumeId) {
      return;
    }

    const hadExistingText = Boolean(resumeText.trim());

    setLoadingSavedResume(true);
    try {
      const savedResume = await api.getSavedResume(savedResumeId);
      setResumeText(savedResume.resume_text || '');
      setSelectedSavedResumeId(savedResumeId);
      setPendingUploadedResumeSave(null);
      setReplacementNotice(hadExistingText ? 'Previous text was replaced with the saved resume.' : '');
    } catch (error) {
      if (getApiErrorCode(error) === 'saved_resumes_disabled') {
        hideSavedResumeUi();
        return;
      }

      setSelectedSavedResumeId('');
      toast.error(getApiErrorMessage(error, 'Failed to load saved resume'));
    } finally {
      setLoadingSavedResume(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isSupportedUploadFile(file)) {
      toast.error(getUnsupportedUploadFormatMessage());
      return;
    }

    const hadExistingText = Boolean(resumeText.trim());
    setUploadingFile(true);
    try {
      const data = await api.uploadResume(file);
      setResumeText(data.text);
      setReplacementNotice(savedResumesEnabled && hadExistingText ? 'Previous text was replaced with the uploaded resume.' : '');
      setSelectedSavedResumeId('');
      setPendingUploadedResumeSave(
        savedResumesEnabled && data.text?.trim()
          ? {
              resumeText: data.text,
              filename: data.filename || file.name,
            }
          : null
      );
      toast.success(`Extracted text from ${data.filename}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to upload file'));
    } finally {
      setUploadingFile(false);
    }
  };

  const handleGenerate = async () => {
    if (!resumeText.trim()) {
      toast.error('Please enter or upload your resume');
      return;
    }

    if (!jobDescription.trim()) {
      toast.error('Please enter the job description');
      return;
    }

    setLoading(true);
    try {
      const resumeTextToUse = await attemptSavePendingUploadedResume(resumeText);
      const data = await api.generateCoverLetter(resumeTextToUse, jobDescription);
      setGeneratedCoverLetter(data.cover_letter);
      toast.success('Cover letter generated!');
    } catch (error) {
      if (isEmailVerificationRequiredError(error)) {
        toast.error(error.response?.data?.detail || error.message || 'Please verify your email before using this feature.');
        navigate('/dashboard');
      } else if (error.response?.status === 403) {
        toast.error(error.response.data.detail);
        navigate('/pricing');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate cover letter');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedCoverLetter);
    toast.success('Copied to clipboard!');
  };

  const handleExportCoverLetter = async (format) => {
    setExportLoading(true);
    try {
      const data = await api.exportCoverLetter(generatedCoverLetter, format);
      const blob = await api.downloadProtectedFile(`/api/cover-letter/download/${data.download_id}`);

      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);

      setShowExportDropdown(false);
      toast.success(`${format.toUpperCase()} download started`);
    } catch (error) {
      if (isEmailVerificationRequiredError(error)) {
        toast.error(error.response?.data?.detail || error.message || 'Please verify your email before using this feature.');
        navigate('/dashboard');
      } else {
        toast.error(error.response?.data?.detail || error.message || 'Export failed');
      }
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface">
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <MainMenu />
          <button
            onClick={() => navigate('/dashboard')}
            className="text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold">Cover Letter Generator</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {!hasPremiumAccess ? (
              <div className="bg-white border border-border rounded-xl p-8 shadow-soft text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Crown className="w-6 h-6" />
              </div>
              <h1 className="font-heading text-2xl font-bold mb-3">Premium Feature</h1>
              <p className="text-muted-foreground max-w-xl mx-auto mb-6">
                Cover letter generation is available on the Premium plan. Upgrade to create tailored cover letters from your resume and target job description.
              </p>
              <button
                onClick={() => navigate('/pricing')}
                className="bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-hover transition-colors"
                data-testid="upgrade-cover-letter-btn"
              >
                View Premium Plan
              </button>
            </div>
          ) : !generatedCoverLetter ? (
            <>
              <div className="flex items-start gap-2 p-3 bg-surface border border-border rounded-lg text-sm">
                <Info className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                <p className="text-muted-foreground">
                  Cover letters are generated from your resume and the job description. Avoid including sensitive personal data.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-border rounded-xl p-6 shadow-soft">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="font-heading text-lg font-semibold">Your Resume</h2>
                      <p className="text-sm text-muted-foreground">Paste your resume or upload a file</p>
                    </div>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingFile}
                      className="flex items-center gap-2 text-primary hover:text-primary-hover transition-colors text-sm"
                      data-testid="upload-btn"
                    >
                      {uploadingFile ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      Upload File
                    </button>
                  </div>
                  {savedResumeUiEnabled && (
                    <SavedResumeSelector
                      savedResumes={savedResumes}
                      selectedSavedResumeId={selectedSavedResumeId}
                      onSelectSavedResume={handleSavedResumeSelect}
                      loading={savedResumesLoading}
                      disabled={uploadingFile || loadingSavedResume}
                    />
                  )}
                  <p className="text-xs text-muted-foreground mb-3">
                    Supported: {SUPPORTED_UPLOAD_FORMAT_LABEL}. {LEGACY_DOC_GUIDANCE}
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={UPLOAD_ACCEPT}
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <textarea
                    value={resumeText}
                    onChange={(e) => {
                      setResumeText(e.target.value);
                      setReplacementNotice('');
                      setPendingUploadedResumeSave(null);
                      setSelectedSavedResumeId('');
                    }}
                    placeholder="Paste your resume text here..."
                    className="w-full h-96 p-4 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none font-mono text-sm"
                    data-testid="resume-textarea"
                  />
                  {savedResumesEnabled && replacementNotice && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {replacementNotice}
                    </p>
                  )}
                </div>

                <div className="bg-white border border-border rounded-xl p-6 shadow-soft">
                  <div className="mb-4">
                    <h2 className="font-heading text-lg font-semibold">Target Job Description</h2>
                    <p className="text-sm text-muted-foreground">Paste the role you are applying for</p>
                  </div>
                  <textarea
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    placeholder="Paste the job description here..."
                    className="w-full h-96 p-4 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none font-mono text-sm"
                    data-testid="job-textarea"
                  />
                </div>
              </div>

              <button
                onClick={handleGenerate}
                disabled={loading || !resumeText.trim() || !jobDescription.trim()}
                className="w-full bg-gradient-to-r from-primary to-accent text-white py-4 rounded-xl font-medium hover:opacity-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="generate-cover-letter-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating with Claude AI...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate Cover Letter
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <AIDisclosure />

              <div className="bg-white border border-border rounded-xl p-6 shadow-soft">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-lg font-semibold">Your Generated Cover Letter</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCopy}
                      className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm"
                      data-testid="copy-cover-letter-btn"
                    >
                      <Copy className="w-4 h-4" />
                      Copy
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setShowExportDropdown(!showExportDropdown)}
                        disabled={exportLoading}
                        className="flex items-center gap-2 text-primary hover:text-primary-hover transition-colors text-sm"
                        data-testid="export-cover-letter-btn"
                      >
                        {exportLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        Export
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      {showExportDropdown && (
                        <div className="absolute right-0 top-full mt-1 bg-white border border-border rounded-lg shadow-medium py-1 z-10 min-w-[120px]">
                          <button
                            onClick={() => handleExportCoverLetter('docx')}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-surface transition-colors"
                            data-testid="export-cover-letter-docx"
                          >
                            DOCX (Word)
                          </button>
                          <button
                            onClick={() => handleExportCoverLetter('pdf')}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-surface transition-colors"
                            data-testid="export-cover-letter-pdf"
                          >
                            PDF
                          </button>
                          <button
                            onClick={() => handleExportCoverLetter('txt')}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-surface transition-colors"
                            data-testid="export-cover-letter-txt"
                          >
                            TXT (Plain)
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mb-4 text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">Note:</span> If you rename an exported Word file, keep the .docx extension. Your browser may still ask where to save the file.
                </div>

                <div
                  className="whitespace-pre-wrap rounded-lg border border-border bg-surface p-6 text-sm leading-7 text-foreground"
                  data-testid="generated-cover-letter"
                >
                  {generatedCoverLetter}
                </div>
              </div>

              <button
                onClick={() => {
                  setGeneratedCoverLetter('');
                  setShowExportDropdown(false);
                }}
                className="w-full bg-white border border-border py-3 rounded-xl font-medium hover:bg-surface transition-all flex items-center justify-center gap-2"
                data-testid="regenerate-cover-letter-btn"
              >
                <RefreshCw className="w-5 h-5" />
                Generate New Cover Letter
              </button>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}
